/**
 * Objectif « 3 soirées pendant l'essai » (`clients/{id}.trialGoal`).
 *
 * Écrit UNIQUEMENT par un job serveur (unboared-b2b), et seulement pour les
 * essais du groupe traité. Champ absent (abonné, groupe témoin, ancien client)
 * ou illisible → `null` : la console n'affiche RIEN. Après `trialEnd` → `null`
 * aussi, même si le job n'a pas encore nettoyé le champ.
 *
 * Jamais affiché sur la TV (`/app`, ScreenApp) : elle est vue par les clients
 * du bar. Seulement la télécommande animateur et la page Mon compte.
 */
export type TrialGoalNext = 'blindtest' | 'cityquiz' | 'mix';

export type TrialGoal = {
    /** Soirées d'au moins 5 joueurs depuis le début de l'essai. */
    nights: number;
    /** Objectif (3). */
    target: number;
    /** Idée de la prochaine soirée ; null = objectif atteint (ou pas d'idée). */
    next: TrialGoalNext | null;
    /** Fin de l'essai, en millisecondes. */
    trialEnd: number;
};

const NEXT_VALUES: readonly TrialGoalNext[] = ['blindtest', 'cityquiz', 'mix'];

type FirestoreDateLike = { toMillis?: () => number; seconds?: number } | Date | number | null | undefined;

const toMillis = (value: FirestoreDateLike): number | null => {
    if (value == null) return null;
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) return null;
        return value < 1e11 ? value * 1000 : value; // secondes ou millisecondes
    }
    if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.getTime() : null;
    if (typeof value !== 'object') return null;
    try {
        if (typeof value.toMillis === 'function') {
            const ms = value.toMillis();
            return typeof ms === 'number' && Number.isFinite(ms) ? ms : null;
        }
    } catch {
        return null;
    }
    if (typeof value.seconds === 'number' && Number.isFinite(value.seconds)) return value.seconds * 1000;
    return null;
};

const isCount = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 0;

/**
 * `clients/{id}.trialGoal` brut → TrialGoal, ou undefined si absent ou de forme
 * inattendue. Un `next` inconnu devient null (le compteur reste affichable,
 * sans idée). La date de fin n'est PAS vérifiée ici (cf `activeTrialGoal`) :
 * le client peut rester ouvert des heures sans nouvel instantané Firestore.
 */
export function trialGoalFromFirestore(raw: unknown): TrialGoal | undefined {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
    const data = raw as Record<string, unknown>;
    const { nights, target } = data;
    if (!isCount(nights) || !isCount(target) || target < 1 || target > 10 || nights > 1000) return undefined;
    const trialEnd = toMillis(data.trialEnd as FirestoreDateLike);
    if (trialEnd == null) return undefined;
    const next = NEXT_VALUES.includes(data.next as TrialGoalNext) ? (data.next as TrialGoalNext) : null;
    return { nights, target, next, trialEnd };
}

/** L'objectif à afficher maintenant : null s'il n'y en a pas ou si l'essai est fini. */
export function activeTrialGoal(goal: TrialGoal | null | undefined, now: number = Date.now()): TrialGoal | null {
    if (!goal || !(now < goal.trialEnd)) return null;
    return goal;
}

/** Objectif atteint : plus rien à proposer sur la télécommande. */
export const isTrialGoalReached = (goal: TrialGoal): boolean => goal.nights >= goal.target;

/** Jeu que l'action de la télécommande met à l'écran, selon l'idée du jour. */
export const TRIAL_GOAL_GAME: Record<TrialGoalNext, string> = {
    blindtest: 'unblind-test',
    cityquiz: 'unquiz',
    // Soirée mix : Petit Bac d'abord, puis Blind Test (les joueurs restent connectés).
    mix: 'petit-bac',
};

/**
 * Carte de la télécommande : à montrer seulement pendant l'essai, objectif non
 * atteint et une idée connue. Renvoie de quoi l'afficher, ou null.
 */
export function trialGoalNudge(goal: TrialGoal | null | undefined): {
    night: number;
    target: number;
    next: TrialGoalNext;
    gameID: string;
} | null {
    if (!goal || isTrialGoalReached(goal) || !goal.next) return null;
    return { night: goal.nights + 1, target: goal.target, next: goal.next, gameID: TRIAL_GOAL_GAME[goal.next] };
}

/**
 * Ce que fait le lien de la carte, en réutilisant les gestes du cockpit :
 * - `pick` : mettre le jeu à l'écran, comme un choix dans le catalogue ;
 * - `openRegie` : UnQuiz est déjà à l'écran → ouvrir la régie (création de quiz par IA) ;
 * - null : pas de lien (jeu absent du catalogue du lieu, ou déjà à l'écran —
 *   le gros bouton « Lancer » est alors juste en dessous).
 */
export type TrialGoalAction = 'pick' | 'openRegie' | null;

export function trialGoalAction(
    next: TrialGoalNext,
    currentGameID: string | undefined,
    inCatalog: boolean
): TrialGoalAction {
    const gameID = TRIAL_GOAL_GAME[next];
    if (currentGameID === gameID) return next === 'cityquiz' ? 'openRegie' : null;
    return inCatalog ? 'pick' : null;
}

/**
 * Ligne du bloc « Essai gratuit » de Mon compte (clés `trialGoal.account.*`).
 * null = rien à afficher. L'idée est une clé courte `trialGoal.idea.<next>`.
 */
export function trialGoalLine(goal: TrialGoal | null | undefined): {
    key: 'trialGoal.account.reached' | 'trialGoal.account.next' | 'trialGoal.account.progress';
    values: { nights: number; target: number };
    idea: TrialGoalNext | null;
} | null {
    if (!goal) return null;
    if (isTrialGoalReached(goal)) {
        return { key: 'trialGoal.account.reached', values: { nights: goal.target, target: goal.target }, idea: null };
    }
    const values = { nights: goal.nights, target: goal.target };
    if (goal.next) return { key: 'trialGoal.account.next', values, idea: goal.next };
    return { key: 'trialGoal.account.progress', values, idea: null };
}
